/**
 * Client-side PDF compression for the Learning Supports upload flow.
 * Checks PDF/image quality first (file size + page count); only reduces size when PDF is large.
 */

// Thresholds: below these we keep higher quality, above we reduce size
const SMALL_PDF_SIZE_BYTES = 1.5 * 1024 * 1024; // 1.5 MB
const SMALL_PDF_MAX_PAGES = 4;

/**
 * Decide render/compression settings based on PDF size and page count.
 * Small PDFs (few pages, small file) keep higher quality; large PDFs get reduced size.
 *
 * @param {number} fileSizeBytes - PDF file size in bytes
 * @param {number} numPages - Number of pages
 * @returns {{ scale: number, jpegQuality: number, reduceSize: boolean }}
 */
export function getPdfQualitySettings(fileSizeBytes, numPages) {
  const isSmall = (fileSizeBytes < SMALL_PDF_SIZE_BYTES && numPages <= SMALL_PDF_MAX_PAGES);
  if (isSmall) {
    return { scale: 1.3, jpegQuality: 0.75, reduceSize: false };
  }
  return { scale: 1.25, jpegQuality: 0.72, reduceSize: true };
}

/**
 * Decide render settings for Word docs (no page count; use file size only).
 * Small files keep higher quality; large files get reduced image size for upload.
 *
 * @param {number} fileSizeBytes - Word file size in bytes
 * @returns {{ scale: number, jpegQuality: number }}
 */
export function getWordQualitySettings(fileSizeBytes) {
  const isSmall = fileSizeBytes < SMALL_PDF_SIZE_BYTES;
  if (isSmall) {
    return { scale: 1.20, jpegQuality: 0.70 };
  }
  return { scale: 1.20, jpegQuality: 0.70 };
}

/**
 * Check if a file is a PDF by type or extension.
 * @param {File} file
 * @returns {boolean}
 */
export function isPdfFile(file) {
  if (!file || !file.name) return false;
  const type = (file.type || "").toLowerCase();
  const name = (file.name || "").toLowerCase();
  return (
    type === "application/pdf" ||
    name.endsWith(".pdf")
  );
}

/** Load PDF.js from CDN if not already on window */
function loadPdfJs() {
  if (typeof window !== "undefined" && window.pdfjsLib) {
    return Promise.resolve(window.pdfjsLib);
  }
  return new Promise((res, rej) => {
    const script = document.createElement("script");
    script.src = "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js";
    script.onload = () => {
      window.pdfjsLib.GlobalWorkerOptions.workerSrc =
        "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js";
      res(window.pdfjsLib);
    };
    script.onerror = () => rej(new Error("Failed to load PDF.js."));
    document.head.appendChild(script);
  });
}

/**
 * Compress a PDF to target ~50% size reduction.
 * Renders each page at lower resolution (scale 1.25) as JPEG (quality 0.72), then rebuilds PDF with jsPDF.
 * If the result is not smaller than the original, the original file is returned.
 *
 * @param {File} file - PDF file to compress
 * @returns {Promise<File>} - Compressed PDF as a new File, or the original file
 */
export async function compressPdfFile(file) {
  if (!file || !isPdfFile(file)) return file;

  try {
    const pdfjsLib = await loadPdfJs();
    const arrayBuffer = await file.arrayBuffer();
    const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer });
    const pdf = await loadingTask.promise;
    const numPages = pdf.numPages;
    if (numPages === 0) return file;

    const { scale, jpegQuality, reduceSize } = getPdfQualitySettings(file.size, numPages);
    if (!reduceSize) return file;

    const { jsPDF } = await import("jspdf");

    let doc = null;
    for (let i = 1; i <= numPages; i++) {
      const page = await pdf.getPage(i);
      const viewport = page.getViewport({ scale });
      const canvas = document.createElement("canvas");
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      const ctx = canvas.getContext("2d");
      await page.render({ canvasContext: ctx, viewport }).promise;

      const imgData = canvas.toDataURL("image/jpeg", jpegQuality);
      const w = viewport.width;
      const h = viewport.height;

      if (i === 1) {
        doc = new jsPDF({ unit: "pt", format: [w, h], hotfixes: ["px_scaling"] });
        doc.addImage(imgData, "JPEG", 0, 0, w, h, undefined, "FAST");
      } else {
        doc.addPage([w, h]);
        doc.addImage(imgData, "JPEG", 0, 0, w, h, undefined, "FAST");
      }
    }

    const blob = doc.output("blob");
    const compressedSize = blob.size;
    const originalSize = file.size;

    // Use compressed version if it's at least ~50% smaller (or any size reduction)
    if (compressedSize < originalSize) {
      return new File([blob], file.name, { type: "application/pdf", lastModified: Date.now() });
    }
    return file;
  } catch (err) {
    console.warn("PDF compression failed, using original file:", err?.message || err);
    return file;
  }
}

/**
 * Compress all PDF files in a list; non-PDF files are returned unchanged.
 *
 * @param {File[]} files - List of files (PDF and/or Word)
 * @returns {Promise<File[]>} - Same list with PDFs replaced by compressed versions when smaller
 */
export async function compressPdfFiles(files) {
  if (!files || !Array.isArray(files) || files.length === 0) return files;
  return Promise.all(
    files.map((file) => (isPdfFile(file) ? compressPdfFile(file) : Promise.resolve(file)))
  );
}
